import { useTranslation } from '@thoughtspot/i18n';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { Card, CardBody, CardHeader, } from '@thoughtspot/radiant-react/widgets/card';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { Toggle } from '@thoughtspot/radiant-react/widgets/toggle';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import React, { useCallback, useMemo, useState } from 'react';
import { useGetOrgsListQuery } from '../../../../services/org/org-service';
import { FeatureManagementTranslationKeys } from '../../constants';
import { ConfigureOrgsModal } from '../../feature-management/configure-orgs-modal/configure-orgs-modal';
import { OrgsList } from '../../orgs-list';
import styles from './edit-orgs-cards.module.scss';
const ALL_ORGS_INPUT = {
    batchsize: 10000,
    offset: 0,
    sort: 'DEFAULT',
    sortascending: true,
};
const useAllOrgs = (providedOrgs) => {
    const skip = providedOrgs !== undefined;
    const { data, loading } = useGetOrgsListQuery(ALL_ORGS_INPUT, skip);
    const fetchedOrgs = useMemo(() => {
        if (skip)
            return [];
        const list = data?.Data?.orgs;
        if (!list)
            return [];
        return list
            .filter(Boolean)
            .map(o => ({ id: o.orgId, name: o.orgName }));
    }, [data, skip]);
    return {
        orgs: skip ? providedOrgs : fetchedOrgs,
        loading: skip ? false : loading,
    };
};
const OrgsBody = ({ orgNames, buttonLabel, onEditClick, testId }) => {
    const { t } = useTranslation();
    return (React.createElement(Horizontal, { className: styles.orgsRow, vAlignContent: "center" },
        React.createElement(Horizontal, { className: styles.orgsText, vAlignContent: "center", spacing: "b" }, orgNames.length > 0 ? (React.createElement(OrgsList, { orgs: orgNames })) : (React.createElement(Typography, { variant: "body-normal", noMargin: true }, t(FeatureManagementTranslationKeys.NO_ORG_SELECTED)))),
        React.createElement(Button, { type: "tertiary", size: "m", text: buttonLabel, onClick: onEditClick, "data-testid": testId })));
};
export const EditOrgsCard = props => {
    const { title, description, selectedOrgIds, allOrgs: providedOrgs, onSave, learnMoreLink, featureDescription, } = props;
    const testId = props['data-testid'];
    const { t } = useTranslation();
    const { orgs, loading: orgsLoading } = useAllOrgs(providedOrgs);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const handleSave = useCallback((ids) => {
        onSave(ids);
        setIsModalOpen(false);
    }, [onSave]);
    const selectedOrgNames = useMemo(() => selectedOrgIds
        .map(id => orgs.find(o => o.id === id)?.name)
        .filter((n) => Boolean(n)), [selectedOrgIds, orgs]);
    const buttonLabel = selectedOrgIds.length > 0
        ? t(FeatureManagementTranslationKeys.EDIT_ORGS)
        : t(FeatureManagementTranslationKeys.ALLOCATE_ORGS);
    return (React.createElement(React.Fragment, null,
        React.createElement(Card, { className: styles.cardWrapper, isTabDisabled: true, noBorder: true, "data-testid": testId },
            React.createElement(CardHeader, { showDivider: true },
                React.createElement(Vertical, { spacing: "b" },
                    React.createElement(Typography, { variant: "content-label" }, title),
                    description && (React.createElement(Typography, { variant: "body-normal", color: Colors.info, ellipsis: { rows: 2 } }, description)))),
            React.createElement(CardBody, null,
                React.createElement(OrgsBody, { orgNames: selectedOrgNames, buttonLabel: buttonLabel, onEditClick: () => setIsModalOpen(true), testId: testId ? `${testId}-edit-orgs` : undefined }))),
        React.createElement(ConfigureOrgsModal, { isOpen: isModalOpen, featureName: title, featureDescription: featureDescription, learnMoreLink: learnMoreLink, orgs: orgs, selectedOrgIds: selectedOrgIds, isLoading: orgsLoading, hasExistingOrgs: selectedOrgIds.length > 0, onDismiss: () => setIsModalOpen(false), onSave: handleSave })));
};
export const EditOrgsWithToggleCard = props => {
    const { title, description, disabled, checked, name, selectedOrgIds, allOrgs: providedOrgs, onSave, onDisable, learnMoreLink, featureDescription, } = props;
    const testId = props['data-testid'];
    const { t } = useTranslation();
    const { orgs, loading: orgsLoading } = useAllOrgs(providedOrgs);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const handleToggleChange = useCallback((e) => {
        const target = e.target;
        if (target.checked) {
            setIsModalOpen(true);
        }
        else {
            onDisable();
        }
    }, [onDisable]);
    const handleSave = useCallback((ids) => {
        onSave(ids);
        setIsModalOpen(false);
    }, [onSave]);
    const selectedOrgNames = useMemo(() => selectedOrgIds
        .map(id => orgs.find(o => o.id === id)?.name)
        .filter((n) => Boolean(n)), [selectedOrgIds, orgs]);
    const editButtonLabel = selectedOrgIds.length > 0
        ? t(FeatureManagementTranslationKeys.EDIT_ORGS)
        : t(FeatureManagementTranslationKeys.ALLOCATE_ORGS);
    return (React.createElement(React.Fragment, null,
        React.createElement(Card, { className: styles.cardWrapper, isTabDisabled: true, noBorder: true, "data-testid": testId },
            React.createElement(CardHeader, { showDivider: checked },
                React.createElement(Horizontal, { vAlignContent: "top", spacing: "b", className: styles.cardHeaderRow },
                    React.createElement(Vertical, { spacing: "b", grow: true, className: styles.headerContent },
                        React.createElement(Typography, { variant: "content-label" }, title),
                        description && (React.createElement(Typography, { variant: "body-normal", color: Colors.info, ellipsis: { rows: 2 } }, description))),
                    React.createElement("div", { className: styles.controlContainer },
                        React.createElement(Toggle, { label: title, hideLabel: true, checked: checked, onChange: handleToggleChange, name: name, disabled: disabled })))),
            checked && (React.createElement(CardBody, null,
                React.createElement(OrgsBody, { orgNames: selectedOrgNames, buttonLabel: editButtonLabel, onEditClick: () => setIsModalOpen(true), testId: testId ? `${testId}-edit-orgs` : undefined })))),
        React.createElement(ConfigureOrgsModal, { isOpen: isModalOpen, featureName: title, featureDescription: featureDescription, learnMoreLink: learnMoreLink, orgs: orgs, selectedOrgIds: selectedOrgIds, isLoading: orgsLoading, isEarlyAccess: !checked, hasExistingOrgs: selectedOrgIds.length > 0, onDismiss: () => setIsModalOpen(false), onSave: handleSave })));
};
//# sourceMappingURL=edit-orgs-cards.container.js.map